import React, { useEffect, useState } from 'react';
import clsx from "clsx";
import { useIntl } from "react-intl";
import { useNavigate, useParams } from "react-router-dom";
import { Divider, Form, Spin, Input, Radio, Space, Button as AntdButton, Avatar, Steps } from "antd";
import {
    MinusCircleOutlined,
    PlusOutlined
} from "@ant-design/icons";
import styled from "styled-components";
import { getContent, toggleSpinner, editContent, setContent, getOpportunity, getProgramById } from "../../redux/actions";
import StyledText from "../../components/StyledText";
import Button from "../../components/Button";
import { getLocalStorageItem } from "../../utils/utilities";
import {
    toggleSpinnerSelector,
    opportunityDataSelector,
    savedContentListSelector,
    savedContentDataSelector,
} from "../../redux/selectors";
import outcomeIcon from "../../assets/outcomeIcon.svg";
import {
    useAppDispatch,
    useAppSelector
} from "../../redux/store";

const formItemLayout = {
    labelCol: { span: 8 },
    wrapperCol: { span: 12 },
};

const CharityFinalOutput = ({ className }) => {
    const baseClassName = clsx("charityFinalOutput", className);
    const intl = useIntl();
    const [formData, setFormData] = useState({});
    const [expectedOutputError, setExpectedOutputError] = useState(null);
    const [savedClicked, setSavedClicked] = useState(false);
    const opportunityData = useAppSelector(opportunityDataSelector) || {}
    const [form] = Form.useForm();
    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    const showSpinner = useAppSelector(toggleSpinnerSelector);
    const { ID } = useParams();
    const { ID: contentId,
        totalOutputs: savedTotalOutputs,
        differentOutputs
    } = useAppSelector(savedContentDataSelector) || {}
    const { opportunityID } = JSON.parse(getLocalStorageItem('contentData') || '{}');
    const { opportunity:
        {
            expectedOutput,
            programID,
        } = {} }
        = opportunityData || {};
    const { message } = useAppSelector(savedContentListSelector) || {};

    useEffect(() => {
        if (contentId) {
            const data = {
                ...(savedTotalOutputs && { totalOutputs: savedTotalOutputs }),
                ...(differentOutputs === 'Y' && { differentOutputs: 'Y' }),
                ...(differentOutputs === 'N' && { differentOutputs: 'N' }),
            };
            form.setFieldsValue(data);
            setFormData(data);
        }
    }, [contentId]);

    useEffect(() => {
        const validateFormList = () => {
            const expectedOutput = formData?.totalOutputs;
            if (!expectedOutput?.length && formData?.differentOutputs === "Y") {
                setExpectedOutputError('Please enter at least 1 row');
            } else {
                setExpectedOutputError(null);
            }
        };

        validateFormList();
    }, [form, formData]);

    useEffect(() => {
        if (message && !savedClicked) {
            dispatch(setContent(''))
            dispatch(toggleSpinner(false));
            navigate(`/impact-maker/content/final/beneficiaries/${ID}`);
        }
        else if (message && savedClicked) {
            dispatch(setContent(''))
            dispatch(toggleSpinner(false));
            navigate(`/impact-maker/content`);
        }
    }, [message])

    useEffect(() => {
        if (opportunityID) {
            document?.documentElement?.scrollTo({
                top: 0,
                behavior: 'smooth'
            });
            dispatch(getContent({ contentID: ID }));
            dispatch(getOpportunity(opportunityID));
        }
    }, [opportunityID]);


    useEffect(() => {
        if (programID) {
            dispatch(getProgramById(programID));
        }
    }, [programID]);

    const onFinishFailed = (e) => {
        console.log("Failed:", e);
    };

    const handleProceed = (e) => {
        form.validateFields(['differentOutputs']).then(values => {
            const { differentOutputs: formInputsChange, totalOutputs } = formData || {};
            if (formInputsChange === undefined) {
                form.setFields([
                    {
                        name: 'differentOutputs',
                        errors: [intl.formatMessage({ id: 'please_select' })],
                    },
                ]);
                return
            }
            if ((expectedOutputError) && values?.differentOutputs) return;
            let data;
            if (values?.differentOutputs === "N") {
                data = {
                    contentId: ID,
                    totalOutputs: expectedOutput,
                    differentOutputs: 'N',
                    action: "Draft"
                }
            }
            else {
                data = {
                    contentId: ID,
                    totalOutputs: totalOutputs,
                    differentOutputs: 'Y',
                    action: "Draft"
                }
            }
            dispatch(editContent(data));
            dispatch(toggleSpinner(true));
        });
    }

    const handleSaveAndExit = () => {
        setSavedClicked(true);
        setTimeout(() => {
            form.submit();
        }, 300);
    }

    const handlePrevious = () => {
        navigate(`/impact-maker/content/final/inputs/${ID}`)
    }

    const onFormChange = (_, allValues) => {
        setFormData(allValues);
    };

    const { totalOutputs, differentOutputs: formDifferentOutputs } = formData || {};

    return (
        <div className={baseClassName}>
            {showSpinner && (
                <div className="overlay">
                    <Spin className="spinner" size="large" spinning={showSpinner} />
                </div>
            )}
            <Steps
                current={0}
                items={[
                    {
                        title: <StyledText as="p" variant="H3">
                            {intl.formatMessage({ id: "step_1" })}
                        </StyledText>,
                        description: <StyledText as="p" variant="B3c" className='mainTitle'>
                            {intl.formatMessage({ id: "inputs_n_outputs" })}
                        </StyledText>,
                    },
                    {
                        title: <StyledText as="p" variant="H3">
                            {intl.formatMessage({ id: "step_2" })}
                        </StyledText>,
                        description: <StyledText as="p" variant="B3" className='mainTitle'>
                            {intl.formatMessage({ id: "objectives_n_outcomes" })}
                        </StyledText>,
                    },
                    {
                        title: <StyledText as="p" variant="H3">
                            {intl.formatMessage({ id: "step_3" })}
                        </StyledText>,
                        description: <StyledText as="p" variant="B3" className='mainTitle'>
                            {intl.formatMessage({ id: "case_studies" })}
                        </StyledText>,
                    },
                    {
                        title: <StyledText as="p" variant="H3">
                            {intl.formatMessage({ id: "step_4" })}
                        </StyledText>,
                        description: <StyledText as="p" variant="B3" className='mainTitle'>
                            {intl.formatMessage({ id: "next_steps" })}
                        </StyledText>,
                    },
                    {
                        title: <StyledText as="p" variant="H3">
                            {intl.formatMessage({ id: "step_5" })}
                        </StyledText>,
                        description: <StyledText as="p" variant="B3" className='mainTitle'>
                            {intl.formatMessage({ id: "preview_n_submit" })}
                        </StyledText>,
                    },
                ]}
            />
            <div className='contentContainer'>
                <div>
                    <StyledText as="p" variant="H1" className='mainTitle'>
                        {intl.formatMessage({ id: "outputs" })}
                    </StyledText>
                    <Form
                        {...formItemLayout}
                        labelAlign="left"
                        onFinish={handleProceed}
                        onFinishFailed={onFinishFailed}
                        form={form}
                        className="formContainer"
                        scrollToFirstError
                        onValuesChange={onFormChange}
                        initialValues={{
                            totalOutputs: [
                                {
                                    numberOfItems: undefined,
                                    monetaryUnit: "",
                                },
                            ],
                        }}
                    >
                        <div className="mainContentContainer">
                            <div className="contentContainer">
                                <StyledText as="p" variant="B3">
                                    {intl.formatMessage({ id: "below_are_the_outputs_you_shared_in_your_application" })}
                                </StyledText>
                                {expectedOutput?.length > 0 && <div className="whiteSupportContainer volunteeringContainer">
                                    <div className="titleContainer">
                                        <div><Avatar src={outcomeIcon} /></div>
                                        <div>
                                            <StyledText as="p" variant="H3">
                                                {intl.formatMessage({ id: "outputs" })}
                                            </StyledText>
                                        </div>
                                    </div>
                                    <ul>{expectedOutput.map((el) => <li key={el?.outputNumber}><StyledText as="span" variant="B3"><span className="outputNumber">{el?.outputNumber}</span> {el?.outputDescription}</StyledText></li>)}</ul>
                                </div>}
                                <StyledText as="p" variant="B3d">
                                    {intl.formatMessage({ id: "did_your_outputs_change" })}
                                </StyledText>
                                <Form.Item
                                    name="differentOutputs"
                                    rules={[
                                        { required: true, message: intl.formatMessage({ id: "enter" }) }
                                    ]}
                                >
                                    <Radio.Group className="radioGroup">
                                        <Radio className="radio" value="Y">
                                            <StyledText as="p" variant="B3">
                                                {intl.formatMessage({ id: "yes" })}
                                            </StyledText>
                                        </Radio>
                                        <Radio className="radio" value="N">
                                            <StyledText as="p" variant="B3">
                                                {intl.formatMessage({ id: "no" })}
                                            </StyledText>
                                        </Radio>
                                    </Radio.Group>
                                </Form.Item>
                                {formDifferentOutputs === "Y" && <>
                                    <StyledText as="p" variant="B3d">
                                        {intl.formatMessage({ id: "please_update_your_outputs" })}
                                    </StyledText>
                                    <Form.List name="totalOutputs">
                                        {(fields, { add, remove }) => (
                                            <>
                                                {fields.map(({ key, name, ...restField }) => (
                                                    <Space key={key} style={{ display: 'flex', marginBottom: 0 }} className='optionsContainer'>
                                                        <Form.Item
                                                            {...restField}
                                                            name={[name, 'numberOfItems']}
                                                            className="formItemTextFieldPart"
                                                            rules={[
                                                                {
                                                                    required: true,
                                                                    message: intl.formatMessage({ id: "enter" }),
                                                                },
                                                            ]}
                                                        >
                                                            <Input placeholder={intl.formatMessage({ id: "enter_estimated_number" })} />
                                                        </Form.Item>
                                                        <Form.Item
                                                            {...restField}
                                                            name={[name, 'monetaryUnit']}
                                                            className="formItemTextFieldPart"
                                                            rules={[
                                                                {
                                                                    required: true,
                                                                    message: intl.formatMessage({ id: "enter" }),
                                                                },
                                                            ]}
                                                        >
                                                            <Input rows={1} placeholder={intl.formatMessage({ id: "description" })} />
                                                        </Form.Item>
                                                        <MinusCircleOutlined onClick={() => remove(name)} style={{ color: '#04ac9c' }} className='for' />
                                                    </Space>
                                                ))}
                                                {expectedOutputError && (
                                                    <StyledText as="p" variant="B3" className="errorText">
                                                        {expectedOutputError}
                                                    </StyledText>
                                                )}
                                                <Form.Item>
                                                    <AntdButton htmlType='button' className='addButton' type="dashed" onClick={() => add()} block icon={<PlusOutlined fill="red" />}>
                                                        <StyledText as="p" variant="B3">
                                                            {intl.formatMessage({ id: "add" })}
                                                        </StyledText>
                                                    </AntdButton>
                                                </Form.Item>
                                            </>
                                        )}
                                    </Form.List>
                                    <StyledText as="p" variant="B3">{intl.formatMessage({ id: "your_outputs_will_show_like_this_in_your_report" })}</StyledText>
                                    {totalOutputs?.map((el) => (el?.numberOfItems && el?.monetaryUnit) ? <StyledText key={el?.numberOfItems} as="p" variant="B3b" className='helperText'>{el?.numberOfItems} {el?.monetaryUnit}</StyledText> : '')}</>}
                                <div className='buttonContainer'>
                                    <Divider />
                                    <div className='buttonContentContainer'>
                                        <div className='nextAndPreviousContainer'>
                                            <Button
                                                variant="secondary"
                                                type="button"
                                                onClick={() => handlePrevious()}>
                                                {intl.formatMessage({ id: "previous" })}
                                            </Button>
                                            <Button
                                                variant="primary"
                                                htmlType="submit"
                                            >
                                                {intl.formatMessage({ id: "next" })}
                                            </Button>
                                        </div>
                                        <div className="saveAndExitButton">
                                            <Button
                                                variant="secondary"
                                                htmlType="button"
                                                onClick={() => handleSaveAndExit()}>
                                                {intl.formatMessage({ id: "save_n_exit" })}
                                            </Button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div className="buttonContainer">
                                <Divider />
                                <div className="buttonContentContainer">
                                    <div className="nextAndPreviousContainer">
                                        <Button variant="secondary" type="button" onClick={() => handlePrevious()}>
                                            {intl.formatMessage({ id: "previous" })}
                                        </Button>
                                        <Button variant="primary" htmlType="submit">
                                            {intl.formatMessage({ id: "next" })}
                                        </Button>
                                    </div>
                                    <div className="saveAndExitButton">
                                        <Button variant="secondary" htmlType="button" onClick={() => handleSaveAndExit()}>
                                            {intl.formatMessage({ id: "save_n_exit" })}
                                        </Button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </Form>

                </div>
            </div>
        </div>
    )
}

const StyledCharityFinalOutput = styled(CharityFinalOutput)`
  &.charityFinalOutput {
    background: white;
    display: flex;
    flex-direction: column;
    padding: 20px;
    justify-content: space-between;
    margin-top: 50px;
    .whiteSupportContainer{
        background: white;
        box-shadow: 0px 3px 16px #0000000d;
          border-radius: 10px;
          display: flex;
          flex-direction: column;
          gap: 16px;
          padding: 16px;
          width: 35vw;
          &.volunteeringContainer {
         box-shadow: 0px 3px 16px #0000000d;
          border-radius: 10px;
          display: flex;
          flex-direction: column;
          gap: 16px;
        }
    }

    .mainTitle {
      margin-bottom: 20px;
    }

    .formItem .ant-form-item-explain-error {
      margin-top: -20px !important;
    }

    .contentContainer {
      display: flex;
      flex-direction: column;
      gap: 12px;
      min-height: 60vh;
      padding-bottom: 60px;

      .formContainer {
        display: flex;
        gap: 12px;
        flex-direction: column;
      }
    }

    .overlay {
      position: fixed;
      top: 0;
      right: 0;
      bottom: 0;
      left: 0;
      z-index: 1000;
      height: 100%;
      background-color: rgba(255, 255, 255, 0.45);
    }

    .spinner {
      z-index: 2000;
      margin: auto;
      left: 50%;
      right: 50%;
      position: absolute;
      bottom: 50%;
    }

    .radioGroup {
      display: flex;
      flex-direction: column;
    }

    .formItemTextField {
      .ant-form-item-control-input {
        width: 34.5vw !important;
      }
    }

    .formItemTextFieldPart {
      .ant-form-item-control-input {
        width: 16vw !important;
      }
    }

    .helperText {
      color: #5271FF !important;
      font-weight: bold !important;
      margin-bottom: 0px !important;
    }

    .ant-form-item {
      margin-bottom: 10px;
    }

    .formItemInput {
      width: 300px;
    }

    .mainTitle{
        margin-bottom: 20px;
    }
    .errorText{
        color: #ff4d4f;
    }
    .contentContainer{
        display: flex;
    }
    .addButton{
        display: flex;
        align-items: center;
        justify-content: center;
        width: 100%;
    }
    .formContainer{
        display: flex;
        gap: 12px;
        flex-direction: column;
    }
    .optionsContainer{
        display: flex;
        align-items: center;
        gap: 30px;
        .for{
            margin-bottom: 10px;
        }
    }

    .radioGroup {
            display: flex;
            flex-direction: column;
    }

    .buttonContainer{
        display: flex;
        justify-content: center;
        flex-direction: column;
        align-items: center;
        position: fixed;
        width: 100%;
        bottom: 0px;
        background: white;
        margin-left: -110px;
        .buttonContentContainer{
            display: flex;
            justify-content: center;
            width: 100%;
            position: relative;
            .saveAndExitButton{
                position: absolute;
                right: 0;
                margin-bottom: 15px;
            }
            .nextAndPreviousContainer{
                display: flex;
                gap: 20px;
                margin-bottom: 15px;
            }
        }
    }
    .formItemTextField{
        .ant-form-item-control-input{
            width: 34.5vw !important;
        }
    }
    .formItemTextFieldPart{
        .ant-form-item-control-input{
            width: 16vw !important;
        } 
    }
    .helperText {
    color: #5271FF !important;
    font-weight: bold !important;
    margin-bottom: 0px !important;
  }
  .ant-form-item{
    margin-bottom: 10px;
  }
  .formItemInput{
    width: 300px;
  }
  }
`;

export default StyledCharityFinalOutput;
